'use strict';

/**
 * T210 W3 (t2-v-new-recovery-fingerprint-kdf) — stretched, per-account-salted
 * recovery fingerprint.
 *
 * SINGLE SOURCE OF TRUTH for the recovery fingerprint. Three call sites derive
 * it (signup-client, recovery-client, identity-vault); they MUST agree
 * byte-for-byte or signup and recovery desync and recovery bricks — so the
 * derivation lives here, once.
 *
 * BEFORE: recoveryFingerprint = sha256(mnemonic.trim().toLowerCase())
 *   - bare hash, no stretch: a leaked/weak mnemonic is cheap to confirm
 *   - no per-account salt: identical across accounts; precomputable; and it was
 *     returned over the UNAUTHENTICATED /recovery/begin endpoint
 *
 * AFTER: recoveryFingerprint = Argon2id(normalized-mnemonic, salt=saltSeed)
 *   - memory-hard stretch (Argon2id) — the same primitive the vault already
 *     uses for password/seed KDF; reuses kdf.js
 *   - bound to the per-account 16-byte saltSeed already stored in meta, so it is
 *     account-specific (no cross-account precomputation)
 *   - the output stays 64-hex so the wire format, the 64-hex validators, and the
 *     constant-time compare (T210 W1) are all unchanged
 *
 * LEGACY COMPAT (Proven-Path): existing accounts have a bare-sha256 fingerprint
 * stored on disk; their recovery client still sends that bare value and the
 * server's stored==submitted compare matches it unchanged. The server gate does
 * NOT re-derive — it only compares the proof the client sends against the stored
 * value, so legacy and stretched accounts both verify without server branching.
 * New accounts (signup AFTER this change) store + send the stretched form.
 *
 * The KDF PROFILE is pinned to a fixed value (default DESKTOP) — NOT device-
 * detected — so signup-time and recovery-time derivations are deterministic
 * across devices. Tests pass FAST_TEST explicitly for speed.
 */

const crypto = require('crypto');
const { argon2id, KDF_PROFILES } = require('./kdf');

function normalizeMnemonic(mnemonic) {
  if (typeof mnemonic !== 'string') {
    throw new Error('Invalid mnemonic: must be a string');
  }
  return mnemonic.trim().toLowerCase();
}

function toSaltBytes(saltSeed) {
  if (saltSeed instanceof Uint8Array) return saltSeed;
  if (typeof saltSeed === 'string' && saltSeed.length >= 32) {
    // hex (>=16 bytes)
    return Buffer.from(saltSeed, 'hex');
  }
  throw new Error('Invalid saltSeed: must be a hex string of >=16 bytes (or Uint8Array)');
}

/**
 * Derive the stretched, per-account-salted recovery fingerprint.
 *
 * @param {string} mnemonic — the BIP39 mnemonic (normalized internally)
 * @param {object} opts
 * @param {string|Uint8Array} opts.saltSeed — the per-account meta.saltSeed
 * @param {object} [opts.kdfProfile] — Argon2id params; defaults to DESKTOP
 * @returns {string} 64-hex fingerprint
 */
function recoveryFingerprint(mnemonic, opts = {}) {
  const normalized = normalizeMnemonic(mnemonic);
  const saltBytes = toSaltBytes(opts.saltSeed);
  const profile = opts.kdfProfile || KDF_PROFILES.DESKTOP;
  const dk = argon2id(normalized, saltBytes, profile);
  return Buffer.from(dk).toString('hex');
}

/**
 * Legacy bare-sha256 fingerprint (pre-T210-W3). Kept ONLY so derivers can fall
 * back to / compare against the legacy form for accounts created before the
 * stretch. Not used to WRITE new fingerprints.
 */
function legacyRecoveryFingerprint(mnemonic) {
  return crypto.createHash('sha256').update(normalizeMnemonic(mnemonic)).digest('hex');
}

module.exports = {
  recoveryFingerprint,
  legacyRecoveryFingerprint,
  normalizeMnemonic,
};
